import {
  CollabConnectionClient,
  SerializerType,
  type CollabConnectionClientOptions,
  type CollabStateBridge,
  type SendDataArray,
} from '@atlassian/collab-connection-client';

import { WebSocketCodes } from '../network/websocket-codes';

import {
  type Transport,
  type TransportCallbacks,
  type TransportContext,
} from './transport';

// TODO: import this when exposed from collab-connection-client
export enum DisconnectReason {
  CLIENT_DISCONNECT = 'CLIENT_DISCONNECT',
  SERVER_DISCONNECT = 'SERVER_DISCONNECT',
  SOCKET_CLOSED = 'SOCKET_CLOSED',
  SOCKET_ERROR = 'SOCKET_ERROR',
  SOCKET_TIMEOUT = 'SOCKET_TIMEOUT',
  UNKNOWN_DISCONNECT = 'UNKNOWN_DISCONNECT',
}

export function disconnectReasonToWebSocketCode(
  reason: DisconnectReason
): WebSocketCodes {
  switch (reason) {
    case DisconnectReason.CLIENT_DISCONNECT:
      return WebSocketCodes.NORMAL_CLOSURE;
    case DisconnectReason.SERVER_DISCONNECT:
      return WebSocketCodes.EXCEPTION_CLOSURE;
    case DisconnectReason.SOCKET_CLOSED:
      return WebSocketCodes.NORMAL_CLOSURE;
    case DisconnectReason.SOCKET_ERROR:
      return WebSocketCodes.CLIENT_ERROR;
    case DisconnectReason.SOCKET_TIMEOUT:
      return WebSocketCodes.CLIENT_ERROR;
    case DisconnectReason.UNKNOWN_DISCONNECT:
      return WebSocketCodes.CLIENT_ERROR;
  }
}

class StateBridge implements CollabStateBridge<Uint8Array> {
  private batchCount = 0;
  private outgoingBuffer: Uint8Array[] = [];

  enqueue(data: Uint8Array): void {
    this.outgoingBuffer.push(data);
  }

  retrieve(): SendDataArray<Uint8Array> {
    this.batchCount = this.outgoingBuffer.length;
    return {
      name: 'update',
      payload: [...this.outgoingBuffer],
    };
  }

  confirm(): Promise<void> {
    this.outgoingBuffer.splice(0, this.batchCount);
    this.batchCount = 0;
    return Promise.resolve();
  }
}

export class CollabConnectionTransport implements Transport {
  private client: CollabConnectionClient<Uint8Array> | undefined;
  private bridge: StateBridge | undefined;
  private disposed = false;
  private sendScheduled = false;

  constructor(
    private readonly ctx: TransportContext,
    private readonly callbacks: TransportCallbacks
  ) {}

  connect(): void {
    let analyticsWebClient;
    if (!this.ctx.getAnalyticsWebClient) {
      // TODO: make the analytics web client optional in C3
      analyticsWebClient = Promise.resolve({} as any);
      console.error('Not passing analytics client to C3');
    } else {
      analyticsWebClient = this.ctx.getAnalyticsWebClient();
    }

    const bridge = new StateBridge();
    this.bridge = bridge;

    const clientOptions: CollabConnectionClientOptions<Uint8Array> = {
      bridge,
      url: this.ctx.relayDetails.url,
      permissionTokenRefresh: async () => {
        const details = await this.ctx.relaySupplier(
          this.ctx.connectionAttempt,
          this.ctx.signal
        );
        return details.accessToken;
      },
      product: {
        product: 'ncs-canvas',
      },
      serializer: {
        type: SerializerType.JSON,
      },
      getAnalyticsWebClient: analyticsWebClient,
      throwOnNotConnected: false,
    };

    const client = new CollabConnectionClient(clientOptions);
    this.client = client;

    client.on('connected', () => {
      if (!this.disposed) {
        this.callbacks.onOpen();
      }
    });

    client.on('disconnected', (reason: DisconnectReason) => {
      if (!this.disposed) {
        const code = disconnectReasonToWebSocketCode(reason);
        this.callbacks.onClose(code, reason);
      }
    });

    client.on('error', (error: Error) => {
      if (!this.disposed) {
        this.callbacks.onError(error);
      }
    });

    client.on('update', (payload: { data: Uint8Array<ArrayBuffer> }) => {
      if (!this.disposed) {
        this.callbacks.onMessage(payload.data);
      }
    });

    client.on('transient', (payload: { data: Uint8Array<ArrayBuffer> }) => {
      if (!this.disposed) {
        this.callbacks.onMessage(payload.data);
      }
    });

    client.initialize();
  }

  send(data: Uint8Array<ArrayBufferLike>): void {
    this.bridge?.enqueue(data);

    if (this.client && !this.sendScheduled) {
      this.sendScheduled = true;
      queueMicrotask(() => {
        this.sendScheduled = false;
        this.client?.sendThenConfirm();
      });
    }
  }

  sendAwareness(data: Uint8Array<ArrayBufferLike>): void {
    this.client?.send({ name: 'transient', payload: data });
  }

  close(): void {
    this.client?.destroy();
  }

  dispose(): void {
    this.disposed = true;
    this.client?.destroy();
    this.client = undefined;
  }
}

export function createCollabConnectionTransport(
  ctx: TransportContext,
  callbacks: TransportCallbacks
): Transport {
  const transport = new CollabConnectionTransport(ctx, callbacks);
  transport.connect();
  return transport;
}
